import { Routes, Route, useLocation } from 'react-router-dom';
import CookieBanner from './components/CookieBanner';
import MainLayout from './components/layout/MainLayout';
import Properties from './pages/Properties';
import HomePage from './pages/HomePage';
import PropertyDetail from './pages/PropertyDetail';
import PropertyPreview from './pages/PropertyPreview';
import AboutUs from './pages/AboutUs';
import SellWithUs from './pages/SellWithUs';
import ContactPage from './pages/ContactPage';
import Dashboard from './pages/admin/Dashboard';
import PropertiesList from './pages/admin/PropertiesList';
import PropertyForm from './pages/admin/PropertyForm';
import ContactsList from './pages/admin/ContactsList';
import UsersList from './pages/admin/UsersList';
import FeaturesList from './pages/admin/FeaturesList';
import FeatureForm from './pages/admin/FeatureForm';
import ZonesList from './pages/admin/ZonesList';
import ZoneForm from './pages/admin/ZoneForm';
import ProvincesList from './pages/admin/ProvincesList';
import ProvinceForm from './pages/admin/ProvinceForm';
import PropertyTypesList from './pages/admin/PropertyTypesList';
import PropertyTypeForm from './pages/admin/PropertyTypeForm';
import XmlExportSettings from './pages/admin/XmlExportSettings';
import Settings from './pages/admin/Settings';
import CdnMigration from './pages/admin/CdnMigration';
import CMSManagement from './pages/admin/CMSManagement';
import Login from './pages/admin/Login';
import PropertyFeed from './pages/api/PropertyFeed';
import PrivacyPolicy from './pages/seo/PrivacyPolicy';
import CookiePolicy from './pages/seo/CookiePolicy';
import TermsConditions from './pages/seo/TermsConditions';
import SitemapPage from './pages/seo/SitemapPage';
import AgenziaImmobiliareScandicci from './pages/seo/landing/AgenziaImmobiliareScandicci';
import CaseInVenditaScandicci from './pages/seo/landing/CaseInVenditaScandicci';
import VendereCasaScandicci from './pages/seo/landing/VendereCasaScandicci';
import PrivateRoute from './components/PrivateRoute';
import XmlGenerateEndpoint from './pages/api/XmlGenerateEndpoint';

function App() {
  const location = useLocation();
  
  // Check if current path is admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginPage = location.pathname === '/admin/login';
  const isApiRoute = location.pathname.startsWith('/api/');
  const isPreviewRoute = location.pathname.startsWith('/anteprima/');
  const isXmlFeed = location.pathname === '/property-feed.xml' || location.pathname === '/property-feed' || location.pathname.includes('property-feed');

  // If it's the login page, render only the login component
  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/admin/login" element={<Login />} />
      </Routes>
    );
  }

  // Link privato: la scheda vive fuori da MainLayout, senza header, menu,
  // footer e cookie banner. È l'unico modo per non avere navigazione verso il
  // resto del sito da una pagina che non deve nemmeno esistere, per Google.
  if (isPreviewRoute) {
    return (
      <Routes>
        <Route path="/anteprima/:token" element={<PropertyPreview />} />
      </Routes>
    );
  }

  // If it's the XML feed or an API route, render the appropriate API handler
  if (isXmlFeed || isApiRoute) {
    return (
      <Routes>
        <Route path="/property-feed.xml" element={<PropertyFeed />} />
        <Route path="/property-feed" element={<PropertyFeed />} />
        <Route path="/api/property-feed.xml" element={<PropertyFeed />} />
        <Route path="/api/property-feed" element={<PropertyFeed />} />
        <Route path="/.netlify/functions/property-feed" element={<PropertyFeed />} />
        <Route path="/api/xml-generate" element={<XmlGenerateEndpoint />} />
        <Route path="/xml-generate" element={<XmlGenerateEndpoint />} />
      </Routes>
    );
  }

  // For admin routes (except login), we use a different layout
  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/admin/properties" element={<PrivateRoute><PropertiesList /></PrivateRoute>} />
        <Route path="/admin/properties/new" element={<PrivateRoute><PropertyForm /></PrivateRoute>} />
        <Route path="/admin/properties/:id/edit" element={<PrivateRoute><PropertyForm /></PrivateRoute>} />
        <Route path="/admin/features" element={<PrivateRoute><FeaturesList /></PrivateRoute>} />
        <Route path="/admin/features/new" element={<PrivateRoute><FeatureForm /></PrivateRoute>} />
        <Route path="/admin/features/:id/edit" element={<PrivateRoute><FeatureForm /></PrivateRoute>} />
        <Route path="/admin/zones" element={<PrivateRoute><ZonesList /></PrivateRoute>} />
        <Route path="/admin/zones/new" element={<PrivateRoute><ZoneForm /></PrivateRoute>} />
        <Route path="/admin/zones/:id/edit" element={<PrivateRoute><ZoneForm /></PrivateRoute>} />
        <Route path="/admin/provinces" element={<PrivateRoute><ProvincesList /></PrivateRoute>} />
        <Route path="/admin/provinces/new" element={<PrivateRoute><ProvinceForm /></PrivateRoute>} />
        <Route path="/admin/provinces/:id/edit" element={<PrivateRoute><ProvinceForm /></PrivateRoute>} />
        <Route path="/admin/property-types" element={<PrivateRoute><PropertyTypesList /></PrivateRoute>} />
        <Route path="/admin/property-types/new" element={<PrivateRoute><PropertyTypeForm /></PrivateRoute>} />
        <Route path="/admin/property-types/:id/edit" element={<PrivateRoute><PropertyTypeForm /></PrivateRoute>} />
        <Route path="/admin/xml-export" element={<PrivateRoute><XmlExportSettings /></PrivateRoute>} />
        <Route path="/admin/contacts" element={<PrivateRoute><ContactsList /></PrivateRoute>} />
        <Route path="/admin/users" element={<PrivateRoute><UsersList /></PrivateRoute>} />
        <Route path="/admin/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/admin/cms" element={<PrivateRoute><CMSManagement /></PrivateRoute>} />
        {/* <Route path="/admin/migrazione-cdn" element={<PrivateRoute><CdnMigration /></PrivateRoute>} /> — nascosto post-go-live */}
      </Routes>
    );
  }

  // For public routes, use the main layout
  return (
    <div>
      <MainLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/immobili" element={<Properties />} />
          <Route path="/chi-siamo" element={<AboutUs />} />
          <Route path="/vendi-con-noi" element={<SellWithUs />} />
          <Route path="/contatti" element={<ContactPage />} />
          <Route path="/immobile/:id" element={<PropertyDetail />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/termini-condizioni" element={<TermsConditions />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/agenzia-immobiliare-scandicci" element={<AgenziaImmobiliareScandicci />} />
          <Route path="/case-in-vendita-scandicci" element={<CaseInVenditaScandicci />} />
          <Route path="/vendere-casa-scandicci" element={<VendereCasaScandicci />} />
        </Routes>
      </MainLayout>
      <CookieBanner />
    </div>
  );
}

export default App;